/**
 * Frame gallery + lightbox. Port of FrameGallery / FrameLightbox / ThumbImage in
 * rm-ui/src/pages/job_detail.rs. Frames come from `useFrames(id)` which returns
 * `FrameResponse` rows with presigned `download_url` / `thumbnail_url` (C3) — so
 * no separate URL-fetch pass is needed (unlike the Electric-era Dioxus code).
 *
 * Testids: `frame-gallery`, `frame-thumb-{i}` (1-based image_index),
 * `frame-lightbox`, `download-button`, `download-all-button`.
 */
import { useState } from 'react'
import { useFrames } from '../hooks/useFrames'
import { TESTID } from '../lib/testids'
import { Button } from './ui/button'
import { Download, ImageOff } from 'lucide-react'
import type { Frame } from '../lib/schemas'

interface LightboxState {
  imageIndex: number
  downloadUrl: string
}

export function FrameGallery({ jobId }: { jobId: string }) {
  const { data, isPending } = useFrames(jobId)
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)

  // Sort frames by image_index.
  const frames: Frame[] = [...(data?.frames ?? [])].sort(
    (a, b) => a.image_index - b.image_index,
  )
  const frameCount = frames.length

  // Grace period before showing empty state: until the first fetch settles.
  const framesSettled = !!data || !isPending

  const onDownloadAll = () => {
    for (const frame of frames) {
      window.open(frame.download_url, '_blank')
    }
  }

  return (
    <>
      <div
        className="bg-card rounded-lg border border-border p-4"
        data-testid={TESTID.frameGallery}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground">
            Wyrenderowane klatki ({frameCount})
          </h3>
          {frameCount > 0 && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-success hover:text-success/80"
              data-testid={TESTID.downloadAllButton}
              onClick={onDownloadAll}
            >
              <Download className="size-3.5" />
              Pobierz wszystkie
            </Button>
          )}
        </div>

        {/* Loading skeleton */}
        {frameCount === 0 && !framesSettled && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="aspect-video bg-muted rounded-lg animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Thumbnail grid — uses presigned URLs from frames API (no redirect) */}
        {frameCount > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {frames.map((frame) => {
              const frameNum = frame.image_index
              const thumbSrc = frame.thumbnail_url ?? frame.download_url
              return (
                <div
                  key={frameNum}
                  className="group relative cursor-pointer rounded-lg overflow-hidden border border-border hover:border-success transition-colors"
                  data-testid={TESTID.frameThumb(frameNum)}
                  onClick={() =>
                    setLightbox({
                      imageIndex: frameNum,
                      downloadUrl: frame.download_url,
                    })
                  }
                >
                  <ThumbImage
                    src={thumbSrc}
                    fallback={frame.download_url}
                    alt={`Frame ${frameNum}`}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    Klatka {frameNum}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Empty state */}
        {frameCount === 0 && framesSettled && (
          <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground">
            <ImageOff className="size-8" />
            <p className="text-sm">Brak wyrenderowanych klatek</p>
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightbox && (
        <FrameLightbox
          downloadUrl={lightbox.downloadUrl}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  )
}

/** Lightbox for a specific frame using a preloaded download URL. */
function FrameLightbox({
  downloadUrl,
  onClose,
}: {
  downloadUrl: string
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
      data-testid={TESTID.frameLightbox}
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {downloadUrl ? (
          <img
            className="max-w-full max-h-[85vh] rounded-lg"
            src={downloadUrl}
          />
        ) : (
          <div className="w-64 h-48 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        <div className="flex gap-2 justify-center mt-3">
          {downloadUrl && (
            <a
              className="px-3 py-1.5 text-sm bg-success text-success-foreground rounded-lg hover:bg-success/80"
              href={downloadUrl}
              download
              data-testid={TESTID.downloadButton}
            >
              Pobierz
            </a>
          )}
          <button
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white border border-white/20 rounded-lg hover:bg-white/10"
            onClick={onClose}
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}

/** Image with shimmer loading state and fallback on error. */
function ThumbImage({
  src,
  fallback,
  alt,
}: {
  src: string
  fallback: string
  alt: string
}) {
  const [loaded, setLoaded] = useState(false)
  const [currentSrc, setCurrentSrc] = useState(src)

  return (
    <div className="relative w-full aspect-video">
      {/* Shimmer placeholder while image loads */}
      {!loaded && (
        <div className="absolute inset-0 bg-muted animate-pulse rounded-lg" />
      )}
      <img
        className={`w-full aspect-video object-cover ${loaded ? '' : 'opacity-0'}`}
        src={currentSrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (currentSrc !== fallback) setCurrentSrc(fallback)
        }}
      />
    </div>
  )
}
